<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import AddressBar from "@/ui/components/FileBrowser/AddressBar.vue";
import FolderTree from "@/ui/components/FileBrowser/FolderTree.vue";
import FileDetailsTable from "@/ui/components/FileBrowser/FileDetailsTable.vue";
import FileProperties from "@/ui/components/FileBrowser/FileProperties.vue";
import ColumnChooserMenu from "@/ui/components/FileBrowser/ColumnChooserMenu.vue";
import RowActionsMenu from "@/ui/components/FileBrowser/RowActionsMenu.vue";
import ManageFolderDialog from "@/ui/components/FileBrowser/ManageFolderDialog.vue";
import ManageFileDialog from "@/ui/components/FileBrowser/ManageFileDialog.vue";
import ResourceShareDialog from "@/ui/components/FileBrowser/ResourceShareDialog.vue";
import BatchDeleteDialog from "@/ui/components/FileBrowser/BatchDeleteDialog.vue";
import {
	FileTableRow,
	useFileTable,
} from "@/ui/components/FileBrowser/useFileTable";
import {
	FileBrowserAction,
	folderKey,
	ResourceTreeCategory,
} from "@/ui/components/FileBrowser/types";
import {
	fileActionAvailability,
	folderActionAvailability,
} from "@/ui/components/FileBrowser/actionAvailability";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

const props = defineProps<{
	resourceTreeCategory: ResourceTreeCategory;
	actions: FileBrowserAction[];
	createNewOpensExplorer?: boolean;
}>();

const { resourceTreeCategory, actions } = toRefs(props);

const emit = defineEmits(["open-explorer"]);

const { t } = useI18n();

const {
	folders,
	nestedFolders,
	isLoadingFolders,
	getFolders,
	editFolder,
	expandedFolderKeys,
	toggleFolderExpanded,
	locationOf,
	path,
	selectedFolder,
	openFolder,
	sortKey,
	sortDescending,
	scope,
	search,
	modifiedByUserId,
	dateFilterValues,
	tagIds,
	applyColumnFilter,
	hasActiveFilters,
	applyFilters,
	setScope,
	clearFilters,
	total,
	totalItems,
	isLoadingPage,
	lastLoadStatus,
	loadNextPage,
	reloadKeepingSelection,
	setSort,
	columns,
	rows,
	selection,
	selectedFile,
	selectedFiles,
	selectedFolders,
	removal,
	canRemoveSelection,
	isRowSelected,
	fileBeingEdited,
	selectRow,
	clearSelection,
	createNewFile,
	editSelectedFile,
	createNewFolder,
	removeFolder,
	removeSelection,
	resolveBatchDelete,
	openSelectedFile,
	updateSelectedFileDefinition,
	toggleSelectedFileFavorite,
	dragDrop,
	close,
	isManageFolderDialogVisible,
	isManageFileDialogVisible,
	isShareResourceDialogVisible,
	isBatchDeleteDialogVisible,
	folderToEdit,
	hasAction,
	chooserColumns,
	isColumnVisible,
	isColumnLocked,
	toggleColumn,
	resetColumnPreferences,
} = useFileTable({ resourceTreeCategory, actions });

const isColumnMenuOpen = ref(false);
const columnMenuTarget = ref<HTMLElement | [number, number] | undefined>();

const openColumnMenuFromToolbar = (event: MouseEvent): void => {
	columnMenuTarget.value = event.currentTarget as HTMLElement;
	isColumnMenuOpen.value = true;
};

const openColumnMenuAtCursor = (target: [number, number]): void => {
	columnMenuTarget.value = target;
	isColumnMenuOpen.value = true;
};

const isPropertiesSuppressed = ref(false);

const isActionsMenuOpen = ref(false);
const actionsMenuTarget = ref<[number, number] | undefined>();
const actionsMenuFolder = ref<ResourceTreeNodeDO | null>(null);

/**
 * Whether the menu would render anything for this row. RowActionsMenu drops
 * itself when every action is unavailable - a file shared read-only is the case
 * that reaches here - and suppressing the properties panel for a menu that then
 * never appears leaves the right-hand side blank with nothing in its place.
 *
 * Several rows at once have only one action in common, deleting them, so that
 * is all the menu offers - and only when the whole selection may actually go.
 */
const hasRowActions = (row: FileTableRow): boolean => {
	if (selection.value.length > 1) {
		return canRemoveSelection.value;
	}

	return row.kind === "folder"
		? folderActionAvailability(row.folder, hasAction).any
		: fileActionAvailability(row.file, hasAction).any;
};

const openActionsMenuAtCursor = (
	target: [number, number],
	row: FileTableRow
): void => {
	if (!hasRowActions(row)) {
		return;
	}

	isPropertiesSuppressed.value = true;
	actionsMenuTarget.value = target;
	actionsMenuFolder.value = row.kind === "folder" ? row.folder : null;
	isActionsMenuOpen.value = true;
};

/**
 * What may actually move is `moveRules`' business, not this component's -
 * `startDrag` refuses a payload the rules reject, whichever row it came from.
 */
const startRowDrag = (event: DragEvent): void => {
	isPropertiesSuppressed.value = true;

	dragDrop.startDrag(event, {
		files: selectedFiles.value,
		folders: selectedFolders.value,
	});
};

/** The drag is over, so give the panel back. */
const endRowDrag = (): void => {
	isPropertiesSuppressed.value = false;
	dragDrop.endDrag();
};

const selectRowFromTable = (
	index: number,
	modifiers: { ctrl: boolean; shift: boolean }
): void => {
	isPropertiesSuppressed.value = false;
	selectRow(index, modifiers);
};

const openFile = async (file: FileDTO): Promise<void> => {
	selection.value = [{ kind: "file", file }];
	await openSelectedFile();
};

const onSearch = async (value: string): Promise<void> => {
	search.value = value;
	await applyFilters();
};

const goUp = (): void => {
	if (path.value.length > 1) {
		openFolder(path.value[path.value.length - 2]);
	}
};

const isPropertiesVisible = computed(
	() => !!selectedFile.value?.id && !isPropertiesSuppressed.value
);

getFolders();
</script>

<template>
	<manage-folder-dialog
		v-if="isManageFolderDialogVisible"
		v-model="isManageFolderDialogVisible"
		:folder-to-edit="folderToEdit"
		:nodes="folders"
		:resource-tree-category="resourceTreeCategory"
	/>

	<manage-file-dialog
		v-if="isManageFileDialogVisible"
		v-model="isManageFileDialogVisible"
		:nodes="folders"
		:file-to-edit="fileBeingEdited"
		:resource-tree-category="resourceTreeCategory"
		@saved="reloadKeepingSelection"
	/>

	<resource-share-dialog
		v-if="isShareResourceDialogVisible"
		v-model="isShareResourceDialogVisible"
		:resource-to-manage="selectedFile"
		@reload-files-in-selected-folder="loadNextPage"
	/>

	<batch-delete-dialog
		v-if="isBatchDeleteDialogVisible"
		v-model="isBatchDeleteDialogVisible"
		:files="removal.files"
		:folders="removal.folders"
		:resource-tree-category="resourceTreeCategory"
		@confirm="resolveBatchDelete(true)"
		@cancel="resolveBatchDelete(false)"
	/>

	<row-actions-menu
		v-model="isActionsMenuOpen"
		:file="selectedFile"
		:folder="actionsMenuFolder"
		:target="actionsMenuTarget"
		:selection-count="selection.length"
		:can-remove-selection="canRemoveSelection"
		@edit-selected-file="editSelectedFile"
		@share-resource="isShareResourceDialogVisible = true"
		@toggle-favorite-resource="toggleSelectedFileFavorite"
		@remove-file="removeSelection"
		@edit-folder="editFolder"
		@remove-folder="removeSelection()"
	/>

	<v-card class="file-explorer">
		<div class="file-explorer__toolbar py-2 pe-2">
			<address-bar
				class="flex-grow-1"
				:path="path"
				:search="search"
				:scope="scope"
				:has-active-filters="hasActiveFilters"
				:total="total"
				:is-drop-target="dragDrop.isDropTarget"
				@navigate="openFolder"
				@drag-over-crumb="dragDrop.onDragOver"
				@drag-leave-crumb="dragDrop.onDragLeave"
				@drop-on-crumb="dragDrop.onDrop"
				@up="goUp"
				@update:search="onSearch"
				@update:scope="setScope"
				@clear="clearFilters"
			/>

			<v-btn
				icon
				size="small"
				variant="text"
				density="comfortable"
				class="ml-2"
				@click="openColumnMenuFromToolbar"
			>
				<v-icon size="13">fa: fa-light fa-table-columns</v-icon>
				<v-tooltip activator="parent">
					{{ t("fileBrowser.chooseColumns") }}
				</v-tooltip>
			</v-btn>

			<v-btn
				v-if="hasAction('new-folder')"
				prepend-icon="mdi-folder-plus-outline"
				variant="text"
				size="small"
				:text="t('fileBrowser.actions.newFolder')"
				class="ml-2"
				@click="createNewFolder"
			/>
			<v-btn
				v-if="hasAction('new-file-top')"
				size="small"
				class="ml-2"
				:disabled="isLoadingFolders && !createNewOpensExplorer"
				:text="t('fileBrowser.actions.createNew')"
				@click="
					createNewOpensExplorer ? emit('open-explorer') : createNewFile()
				"
			/>
		</div>

		<column-chooser-menu
			v-model="isColumnMenuOpen"
			:columns="chooserColumns"
			:is-column-visible="isColumnVisible"
			:is-column-locked="isColumnLocked"
			:target="columnMenuTarget"
			@toggle="toggleColumn"
			@reset="resetColumnPreferences"
		/>

		<v-divider />

		<div class="file-explorer__body">
			<div class="file-explorer__tree">
				<folder-tree
					v-for="node in nestedFolders"
					:key="folderKey(node)"
					:node="node"
					:selected="selectedFolder"
					:expanded-keys="expandedFolderKeys"
					:is-drop-target="dragDrop.isDropTarget"
					@select="openFolder"
					@toggle="toggleFolderExpanded"
					@edit="editFolder"
					@remove="removeFolder"
					@drag-folder="dragDrop.startFolderDrag"
					@drag-over-folder="dragDrop.onDragOver"
					@drag-leave-folder="dragDrop.onDragLeave"
					@drop-on-folder="dragDrop.onDrop"
					@drag-end="dragDrop.endDrag"
				/>
			</div>

			<div class="file-explorer__table">
				<file-details-table
					:columns="columns"
					:rows="rows"
					:resource-tree-category="resourceTreeCategory"
					:sort-key="sortKey"
					:sort-descending="sortDescending"
					:is-row-selected="isRowSelected"
					:is-loading-page="isLoadingPage"
					:load-more="loadNextPage"
					:last-load-status="lastLoadStatus"
					:location-of="locationOf"
					:date-filter-values="dateFilterValues"
					:modified-by-user-id="modifiedByUserId"
					:tag-ids="tagIds"
					:is-drop-target="dragDrop.isDropTarget"
					@sort="setSort"
					@filter="applyColumnFilter"
					@open-folder="openFolder"
					@select-row="selectRowFromTable"
					@drag-row="startRowDrag"
					@drag-over-row="dragDrop.onDragOver"
					@drag-leave-row="dragDrop.onDragLeave"
					@drop-on-row="dragDrop.onDrop"
					@drag-end="endRowDrag"
					@open-file="openFile"
					@open-column-menu="openColumnMenuAtCursor"
					@open-actions-menu="openActionsMenuAtCursor"
				/>
			</div>

			<div v-if="isPropertiesVisible" class="file-explorer__properties">
				<file-properties
					:resource-tree-category="resourceTreeCategory"
					:selected-file="selectedFile"
					@reload-files-in-selected-folder="loadNextPage"
					@edit-selected-file="editSelectedFile"
					@toggle-favorite-resource="toggleSelectedFileFavorite"
					@close-properties="clearSelection"
					@share-resource="isShareResourceDialogVisible = true"
					@remove-file="removeSelection"
				/>
			</div>
		</div>

		<v-divider />

		<!-- Band 3: status + actions -->
		<div class="file-explorer__footer pa-2">
			<span class="text-caption">
				{{ t("fileBrowser.itemCount", totalItems) }}
				<template v-if="selection.length > 1">
					· {{ t("fileBrowser.selectedCount", selection.length) }}
				</template>
			</span>

			<v-spacer />

			<v-btn
				v-if="canRemoveSelection"
				color="error"
				variant="text"
				:text="t('fileBrowser.actions.delete')"
				@click="removeSelection"
			/>
			<v-btn
				v-if="hasAction('close')"
				color="error"
				variant="text"
				:text="t('fileBrowser.actions.close')"
				@click="close"
			/>
			<v-btn
				v-if="hasAction('open-file')"
				:disabled="!selectedFile"
				:text="t('fileBrowser.actions.open')"
				class="ml-2"
				@click="openSelectedFile"
			/>
			<v-btn
				v-if="hasAction('update-file-definition') && selectedFile"
				:text="t('fileBrowser.actions.update')"
				class="ml-2"
				@click="updateSelectedFileDefinition"
			/>
			<v-btn
				v-if="hasAction('new-file') && !selection.length"
				:disabled="isLoadingFolders"
				:text="t('fileBrowser.actions.saveNew')"
				class="ml-2"
				@click="createNewFile"
			/>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.file-explorer {
	/*
	 * One definition of the column geometry. The toolbar's cells and the body's
	 * panes both read these, so the search box stays exactly as wide as the tree
	 * and the breadcrumb stays exactly over the table.
	 */
	--tree-width: 240px;
	--properties-width: 290px;

	display: flex;
	flex-direction: column;
	height: 70vh;
	max-height: 70vh;

	&__toolbar,
	&__footer {
		display: flex;
		align-items: center;
		flex: none;
		background-color: rgba(var(--color-background), 0.6);
	}

	&__body {
		display: flex;
		flex: 1 1 auto;
		min-height: 0;
	}

	&__tree {
		flex: 0 0 var(--tree-width);
		overflow-y: auto;
		background-color: rgba(var(--color-background), 0.6);
	}

	&__table {
		flex: 1 1 auto;
		min-width: 0;
		overflow: auto;
	}

	&__properties {
		flex: 0 0 var(--properties-width);
		overflow: hidden;
		border-left: 1px solid rgb(var(--color-secondary));
	}
}
</style>
